import React, { useCallback, useMemo, useState } from 'react';
import {
  IMenuAction,
  LEFT_DRAWER_MOBILE_BREAKPOINT,
  Menu,
  useDialogs,
  useLoadingPromise,
  workflowEditQuery,
} from '@diana/core';
import { THING_PAGE_NS } from '../../../../i18n';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { IUseSectionActions } from '../../../../../../hooks/useSectionRename';

export const useEntitySectionActions = ({
  component,
  setIsEditMode,
  layoutId,
  workId,
  buttonSx = {},
  boxSx = {},
}: IUseSectionActions) => {
  const { breakpoints } = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isMobileDrawer = useMediaQuery(breakpoints.down(LEFT_DRAWER_MOBILE_BREAKPOINT));

  const { t } = useTranslation(THING_PAGE_NS);
  const isMenuOpen = Boolean(menuAnchorEl);

  // QUERY MUTATIONS
  const deleteMutation = workflowEditQuery.deleteSection();
  const { mutate: deleteSection, isPending: isDeleting } = useMutation({
    mutationKey: deleteMutation.key,
    mutationFn: deleteMutation.fn,
  });

  const cloneMutation = workflowEditQuery.addSection();
  const { mutate: addSection } = useMutation({
    mutationKey: cloneMutation.key,
    mutationFn: cloneMutation.fn,
  });

  // HANDLERS
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleMenuBtnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setMenuAnchorEl(e.currentTarget);
    },
    [setMenuAnchorEl],
  );

  const handleDelete = useCallback(() => {
    deleteSection({
      rootLayoutId: layoutId,
      workId,
      component: component,
    });
    setMenuAnchorEl(null);
  }, [deleteSection, layoutId, workId, component]);

  const handleEdit = useCallback(() => {
    setIsEditMode(true);
    setMenuAnchorEl(null);
  }, [setIsEditMode]);

  const handleDuplicate = useCallback(() => {
    addSection({
      rootLayoutId: layoutId,
      component: component,
      workId,
    });
    setMenuAnchorEl(null);
  }, [addSection, layoutId, component, workId]);

  // Confirm delete dialog
  const waitForDelete = useLoadingPromise(isDeleting);
  const { confirm } = useDialogs();

  const menuActions = useMemo(() => {
    return [
      {
        title: t('entityMenu.edit'),
        onClick: handleEdit,
        disabled: !component.canEditLabel,
      },
      {
        title: t('entityMenu.duplicate'),
        onClick: handleDuplicate,
        disabled: !component.canCloneComponent,
      },
      {
        title: t('entityMenu.delete'),
        onClick: async () => {
          const { confirmed } = await confirm(null, {
            title: t('entityMenu.confirmDelete', { name: component.label }),
            variant: 'delete',
            onCallback: waitForDelete,
          });
          if (confirmed) handleDelete();
          handleMenuClose();
        },
        color: 'error.main',
        disabled: !component.canCloneComponent,
      },
    ] as IMenuAction[];
  }, [
    t,
    handleEdit,
    component.canEditLabel,
    component.canCloneComponent,
    component.label,
    handleDuplicate,
    confirm,
    waitForDelete,
    handleDelete,
    handleMenuClose,
  ]);

  const containerStyles = {
    position: 'relative',
    '&:hover': {
      '.section-menu': { opacity: 1 },
    },
  };

  const menuActionsComponent = (
    <Box sx={{ position: 'absolute', right: -4, top: 4, ...boxSx }}>
      <IconButton
        className={`${Boolean(menuAnchorEl) ? 'active' : ''} section-menu`}
        aria-label="options"
        color="secondary"
        size="small"
        onClick={handleMenuBtnClick}
        sx={{
          top: 8,
          ml: 1,
          opacity: +isMobileDrawer || +Boolean(menuAnchorEl),
          transition: '.2s opacity',
          willChange: 'opacity',
          ...buttonSx,
        }}
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        open={isMenuOpen}
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        actions={menuActions}
        position="bottomRightInner"
      />
    </Box>
  );

  return {
    menuActionsComponent,
    containerStyles,
  };
};
